import { Component, ViewChild } from '@angular/core';
import { ComponentView } from 'src/modules/app-template/models/component-view.model';
import { DateIntv } from 'src/modules/enaio/shared/DateIntv';
import { EnaioCallSelect } from 'src/modules/enaio/shared/EnaioCallSelect';
import { EnaioDocument } from 'src/modules/enaio/shared/EnaioDocument';
import { SimLogSearchOptions } from 'src/modules/enaio/shared/SimLogSearchOptions';
import { SmFormComponent } from 'src/modules/sm-base/components/sm-form/sm-form.component';
import { FrontendFieldAddon } from 'src/modules/sm-base/models/frontend-field-addon.model';
import { FrontendFieldDefinition } from 'src/modules/sm-base/models/frontend-field-definition.model';
import { FrontendFieldListItem } from 'src/modules/sm-base/models/frontend-field-list-item.model';
import { FrontendFieldType } from 'src/modules/sm-base/models/frontend-field-type.enum';
import { FrontendFormDefinition } from 'src/modules/sm-base/models/frontend-form-definition.model';
import { OrdinaryObject } from 'src/modules/utils/shared/ordinary-object.model';
import { Utils } from 'src/modules/utils/shared/utils';
import { DhTools } from '../../models/dh-tools.model';
import { SimLogQuery } from '../../models/sim-log-query.model';
import { GuiUtils } from 'src/modules/utils/misc/gui-utils';

@Component({
  selector: 'app-sim-log',
  templateUrl: './sim-log.component.html',
  styleUrls: ['./sim-log.component.scss']
})
export class SimLogComponent extends ComponentView {

    simLogDirs: string[];
    simLogQueries: SimLogQuery[];
    formInput: FrontendFormDefinition;
    tabs: {title: string, options: SimLogSearchOptions}[] = [];
    tabIndex = 0;

    @ViewChild("form") form: SmFormComponent;

    async initParams(): Promise<boolean> {
        this.simLogDirs = await DhTools.backendCall("api/dh/getSimLogDirs").listStrings();
        this.simLogQueries = await DhTools.backendCall("api/dh/getSimLogQueries").list(SimLogQuery);

        this.formInput = new FrontendFormDefinition([
            new FrontendFieldDefinition("configNames", "Konfigurationen", FrontendFieldType.comboBoxMulti, { mandatory: true, listItems: this.simLogDirs.map(m => new FrontendFieldListItem(m, m)), value: this.simLogDirs}),
            new FrontendFieldDefinition("beginDate", "Startdatum", FrontendFieldType.datePicker, { value: Utils.dateStartOf(Utils.dateAdd(new Date(), "day", -7), "day"), datePickerWithTime: true}),
            new FrontendFieldDefinition("endDate", "Enddatum", FrontendFieldType.datePicker, { value: Utils.dateEndOf(new Date(), "day"), datePickerWithTime: true,
                addOnButtons: [new FrontendFieldAddon("", "", "fas fa-ellipsis", true)], onAddOnClick: (_, __) => DhTools.showDateIntervalOptions(this.app, this.formInput) }),
            new FrontendFieldDefinition("query", "Abfrage", FrontendFieldType.comboBox, { value: this.simLogQueries[0], mandatory: true, dropdownEditable: false, listItems: this.simLogQueries.map(q => new FrontendFieldListItem(q, q.name))}),
            new FrontendFieldDefinition("conversion", "Ausgabeformat", FrontendFieldType.text)
        ]);

        this.formInput.getField("query").onValueChanged = () => {
            this.formInput.fields = this.formInput.fields.filter(f => !f.id.startsWith("field_"));
            let query = this.formInput.getValue("query") as SimLogQuery;
            if (query != null) {
                for (let match of query.condition.matchAll(/\[\[(.*?)\]\]/g)) {
                    let placeholder = match[1];
                    let newField = new FrontendFieldDefinition("field_" + placeholder, placeholder, FrontendFieldType.text);
                    this.formInput.fields = [...this.formInput.fields, newField];
                    if (placeholder == "Objekt-ID") {
                        newField.addOnButtons = [
                            new FrontendFieldAddon("", "", "fas fa-ellipsis", true)
                        ];
                        newField.onAddOnClick = (addOn, evt) => this.showObjectIdOptions(evt, placeholder);
                    }
                }
            }
            this.formInput.setValue("conversion", query.result);
            this.form.form = this.formInput;
        };
        this.formInput.getField("query").onValueChanged(true);
        return true;
    }

    async run(): Promise<void> {
        let options = this.getSearchOptions();
        if (options != null) {
            this.tabs.push(options);
            GuiUtils.angularTimer(() => this.tabIndex = this.tabs.length - 1);
        }
    }

    showObjectIdOptions(evt: MouseEvent, placeholder: string): void {
        let model = [
            {
                label: "Anlagezeitpunkt übernehmen",
                command: async () => this.useObjectDate(placeholder, 0)
            },
            {
                label: "Änderungszeitpunkt übernehmen",
                command: async () => this.useObjectDate(placeholder, 1)
            },
            {
                label: "Lebensdauer übernehmen",
                command: async () => this.useObjectDate(placeholder, 2)
            }
        ];
        this.app.openContextMenu(model);
    }


    async load(configName: string, query: string, beginDate?: Date, endDate?: Date, fields?: OrdinaryObject): Promise<void> {
        this.formInput.setValue("configNames", [Utils.getFileNameWithoutPath(Utils.stringRemoveSuffix(Utils.stringDef(configName), ".json"))]);
        this.formInput.setValue("query", this.simLogQueries.find(q => q.name == query));
        await this.formInput.getField("query").onValueChanged?.(true);
        if (beginDate != null) {
            this.formInput.setValue("beginDate", beginDate);
        }
        if (endDate != null) {
            this.formInput.setValue("endDate", endDate);
        }
        if (fields != null) {
            for (let prop of Utils.getOwnPropertyNames(fields)) {
                this.formInput.setValue("field_" + prop, fields[prop]);
            }
        }
    }

    async useObjectDate(fieldName: string, type: number): Promise<void> {
        let o = Utils.arrayGetSafe(await DhTools.enaioCall<EnaioDocument[]>(Utils.fromPlain(EnaioCallSelect, {
            selectObjectId: Utils.toNumber(Utils.getSubstringTo(Utils.stringDef(this.formInput.getValue("field_" + fieldName) as string, ""), ";").trim()),
            getBaseParams: true
        })), 0);
        if (o == null) {
            this.app.showToast("error", "Fehler", "Objekt-ID nicht gefunden");
            return;
        }
        this.formInput.setValue("beginDate", Utils.dateAdd(type == 0 || type == 2 ? o.baseParams.creationDate : o.baseParams.modificationDate, "hour", -1));
        this.formInput.setValue("endDate", Utils.dateAdd(type == 0 ? o.baseParams.creationDate : o.baseParams.modificationDate, "hour", 1));
    }

    getSearchOptions(): {title: string, options: SimLogSearchOptions} {
        let query = this.formInput.getValue("query") as SimLogQuery;
        let q = query.condition;
        let titleParts : string[] = [];
        for (let match of query.condition.matchAll(/\[\[(.*?)\]\]/g)) {
            let placeholder = match[1];
            let v = this.formInput.getField("field_" + placeholder).getValue() as string;
            if (placeholder == "Objekt-ID") {
                try {
                    v = Utils.getSubstringTo(this.formInput.getValue("field_" + placeholder) as string, ";").trim();
                }
                catch {
                    this.app.showToast("error", "Fehler", "Die eingegebene Objekt-ID ist ungültig");
                    return null;
                }
            }
            q = q.replace(match[0], v);
            titleParts.push(v);
        }
        
        return {
            title: query.name + (titleParts.length == 0 ? "" : ": " + Utils.arrayItemsToString(titleParts, ", ")),
            options: Utils.fromPlain(SimLogSearchOptions, {
            dateIntvs: [Utils.fromPlain(DateIntv, {
                start: this.formInput.getValue("beginDate"),
                end: this.formInput.getValue("endDate")
            })],
            handlerNamePattern: Utils.arrayItemsToString(this.formInput.getValue("configNames") as string[], "|"),
            query: q,
            conversion: this.formInput.getValue("conversion"),
            returnFullLog: false
        })};
    }

    closeResult(event: any): void {
        this.tabs.splice(event.index - 1, 1);
    }

}
